// src/components/PublicLanguageDropdown.jsx
// =============================================================================
// Public-language dropdown for anonymous/auth-facing pages.
// =============================================================================
//
// PURPOSE
// -------
// This component provides a small, reusable language selector for pages that are
// visible before a user profile is available, for example LoginPage and
// RegisterPage.
//
// IMPORTANT ARCHITECTURAL BOUNDARY
// --------------------------------
// Public pages must not use the authenticated LanguageSwitcher component.
//
// The authenticated LanguageSwitcher is connected to user settings and persists
// the final preference through the backend profile API. Public pages cannot do
// that safely because there may be no authenticated user yet.
//
// LOGIN-SPECIFIC PROFILE MODE
// ---------------------------
// The login page can expose a special "Profile language" option. This option is
// not a language. It is an instruction for the login flow:
//
//   after successful authentication, use user.preferred_language returned by
//   the backend user profile.
//
// Because the profile is not known before authentication, choosing this option
// does not immediately change i18next language. It only changes the login mode.
//
// STORAGE POLICY
// --------------
// This component intentionally does not write to localStorage or sessionStorage.
// Public display language is kept in React/i18next memory for the current page
// lifetime. Durable language preference belongs to the authenticated user
// profile and is changed from UserSettingsPage / authenticated LanguageSwitcher.
//

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LANGUAGES, normalizeLanguage } from "../i18n";

export const PROFILE_LANGUAGE_VALUE = "profile";

export const PUBLIC_LANGUAGE_MODES = Object.freeze({
  PROFILE: "profile",
  MANUAL: "manual",
});

const PUBLIC_LANGUAGE_OPTIONS = [
  {
    value: "pl",
    shortLabel: "PL",
    labelKey: "publicLanguage.polish",
  },
  {
    value: "en",
    shortLabel: "EN",
    labelKey: "publicLanguage.english",
  },
];

function toSelection(value) {
  if (value === PROFILE_LANGUAGE_VALUE) {
    return {
      mode: PUBLIC_LANGUAGE_MODES.PROFILE,
      language: null,
      value: PROFILE_LANGUAGE_VALUE,
    };
  }

  const language = normalizeLanguage(value);
  return {
    mode: PUBLIC_LANGUAGE_MODES.MANUAL,
    language,
    value: language,
  };
}

export function normalizePublicLanguageSelection(value) {
  return toSelection(value);
}

export default function PublicLanguageDropdown({
  value,
  onChange,
  allowProfileLanguage = false,
  className = "register-link",
  style,
}) {
  const { t } = useTranslation("auth");

  const options = useMemo(() => {
    const manualOptions = PUBLIC_LANGUAGE_OPTIONS.filter((option) =>
      SUPPORTED_LANGUAGES.includes(option.value)
    );

    if (!allowProfileLanguage) {
      return manualOptions;
    }

    return [
      {
        value: PROFILE_LANGUAGE_VALUE,
        shortLabel: t("publicLanguage.profileShort"),
        labelKey: "publicLanguage.profile",
      },
      ...manualOptions,
    ];
  }, [allowProfileLanguage, t]);

  const selectedValue = allowProfileLanguage
    ? value || PROFILE_LANGUAGE_VALUE
    : normalizeLanguage(value);

  return (
    <div
      className={className || undefined}
      aria-label={t("publicLanguage.label")}
      style={{
        marginTop: className === "register-link" ? 0 : undefined,
        marginBottom: 14,
        textAlign: "center",
        fontSize: 13,
        color: "#374151",
        ...(style || {}),
      }}
    >
      <label htmlFor="public-language-dropdown" style={{ marginRight: 8 }}>
        {t("publicLanguage.label")}
      </label>

      <select
        id="public-language-dropdown"
        value={selectedValue}
        onChange={(event) => {
          const selection = toSelection(event.target.value);
          onChange?.(selection);
        }}
        title={t("publicLanguage.selectTitle")}
        style={{
          minWidth: allowProfileLanguage ? 178 : 118,
          padding: "4px 8px",
          borderRadius: 8,
          border: "1px solid #d1d5db",
          background: "#ffffff",
          color: "#374151",
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.labelKey)}
          </option>
        ))}
      </select>
    </div>
  );
}
